/**
 * ScheduleSlots List Page
 * List and manage schedule slots
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  MenuItem,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import {
  InfoOutlined,
  Delete as DeleteIcon,
  Block as BlockIcon,
  LockOpen as UnblockIcon,
} from '@mui/icons-material';
import { PageShell, DataTable, PrerequisiteBlock, type DataTableColumn, type PrerequisiteBlockProps } from '../../components';
import { scheduleSlotService } from '../../services/scheduleSlotService';
import { professionalScheduleService } from '../../services/professionalScheduleService';
import { useListaPermitida } from '../../authorization/useListaPermitida';
import { useEscolhaUnica } from '../../hooks/useEscolhaUnica';
import { prerequisiteFromError } from '../../utils/prerequisiteFromError';
import {
  ScheduleSlot,
  ScheduleSlotFilters,
  SlotStatus,
  formatTimeRange,
  formatCapacity,
} from '../../types/scheduleSlot';
import { useAcao } from '../../authorization/useVeredito';
import { ClinicForSchedule } from '../../types/professionalSchedule';
import { ServiceType } from '../../types/provider';
import { SlotStatusChip } from './components/SlotStatusChip';
import { SlotAppointmentTypeChip } from './components/SlotAppointmentTypeChip';
import { ScheduleSlotsFilters as FiltersComponent, SlotFiltersData } from './components/ScheduleSlotsFilters';

const HELP_TEXT =
  'Os horários disponíveis são gerados automaticamente a partir das escalas de atendimento cadastradas para cada profissional. Eles representam os slots individuais em que pacientes podem ser agendados. Para que os horários apareçam, é necessário que a clínica utilize o tipo de atendimento "Agenda dos Profissionais", que existam escalas configuradas e que a agenda tenha sido liberada para o período desejado. Selecione uma clínica abaixo para visualizar os horários gerados.';

const formatDate = (dateString: string): string => {
  if (!dateString) return '-';
  const [year, month, day] = dateString.split('-');
  return `${day}/${month}/${year}`;
};

const getOccupancyColor = (percentage: number): 'success' | 'warning' | 'error' => {
  if (percentage < 50) return 'success';
  if (percentage < 80) return 'warning';
  return 'error';
};

const canBlockSlot = (slot: ScheduleSlot): boolean =>
  slot.isActive && slot.status !== SlotStatus.BLOQUEADO && slot.status !== SlotStatus.CANCELADO;

const canUnblockSlot = (slot: ScheduleSlot): boolean => slot.status === SlotStatus.BLOQUEADO;

const canDeleteSlot = (slot: ScheduleSlot): boolean => slot.currentBookings === 0;

const slotColumns: DataTableColumn<ScheduleSlot>[] = [
  {
    id: 'slotDate',
    label: 'Data',
    primary: true,
    render: (slot) => (
      <Typography variant="body2" fontWeight="medium">
        {formatDate(slot.slotDate)}
      </Typography>
    ),
  },
  {
    id: 'time',
    label: 'Horário',
    render: (slot) => (
      <Typography variant="body2">{formatTimeRange(slot.startTime, slot.endTime)}</Typography>
    ),
  },
  {
    id: 'doctorName',
    label: 'Médico',
    render: (slot) => (
      <Box>
        <Typography variant="body2" fontWeight="medium" noWrap sx={{ maxWidth: 150 }}>
          {slot.doctorName}
        </Typography>
      </Box>
    ),
  },
  {
    id: 'location',
    label: 'Local',
    render: (slot) => (
      <Typography variant="body2" noWrap sx={{ maxWidth: 120 }}>
        {slot.location}
      </Typography>
    ),
  },
  {
    id: 'appointmentType',
    label: 'Tipo',
    render: (slot) => <SlotAppointmentTypeChip type={slot.appointmentType} />,
  },
  {
    id: 'occupancy',
    label: 'Ocupação',
    render: (slot) => (
      <Box sx={{ minWidth: 80 }}>
        <Typography variant="caption">
          {formatCapacity(slot.currentBookings, slot.maxCapacity)}
        </Typography>
        <LinearProgress
          variant="determinate"
          value={slot.occupancyPercentage}
          color={getOccupancyColor(slot.occupancyPercentage)}
          sx={{ height: 6, borderRadius: 1, mt: 0.5 }}
        />
      </Box>
    ),
  },
  {
    id: 'status',
    label: 'Status',
    render: (slot) => <SlotStatusChip status={slot.status} />,
  },
];

export const ScheduleSlotsListPage: React.FC = () => {
  const bloquear = useAcao('schedule_slot.update', 'Bloquear');
  const desbloquear = useAcao('schedule_slot.update', 'Desbloquear');
  const excluir = useAcao('schedule_slot.delete', 'Excluir');
  const [scheduleSlots, setScheduleSlots] = useState<ScheduleSlot[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [prerequisite, setPrerequisite] = useState<PrerequisiteBlockProps | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [apiFilters, setApiFilters] = useState<ScheduleSlotFilters>({});

  // Clinic selector state
  const [selectedClinic, setSelectedClinic] = useState<ClinicForSchedule | null>(null);

  // Block dialog state
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [blockingSlotId, setBlockingSlotId] = useState<number | null>(null);
  const [blockReason, setBlockReason] = useState('');

  // A lista sai do cadastro de prestadores, que é outra permissão — e sem clínica escolhida
  // nenhum horário carrega. O seletor vazio e mudo era indistinguível de "não há clínica".
  const carregarClinicas = useCallback(() => professionalScheduleService.getClinics(), []);
  const clinicas = useListaPermitida<ClinicForSchedule>(
    'provider.read',
    'Clínicas',
    carregarClinicas
  );
  const clinics = clinicas.itens;

  const selecionarClinica = useCallback((clinic: ClinicForSchedule | null) => {
    setSelectedClinic(clinic);
    setApiFilters({});
    setPage(0);
    setPrerequisite(null);
  }, []);

  const { mostrarSeletor: mostrarSeletorDeClinica } = useEscolhaUnica(
    clinics,
    selectedClinic,
    selecionarClinica,
    { resolvida: clinicas.resolvida }
  );

  const loadScheduleSlots = useCallback(async () => {
    if (!selectedClinic) {
      setScheduleSlots([]);
      setTotal(0);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const params = {
        page: page + 1,
        limit: rowsPerPage,
        ...apiFilters,
        clinicId: selectedClinic.id,
      };

      const response = await scheduleSlotService.getScheduleSlots(params);
      setScheduleSlots(response.scheduleSlots);
      setTotal(response.total);
    } catch (err: unknown) {
      const error = err as Error;
      setError(error.message ?? 'Erro ao carregar slots de horário');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, apiFilters, selectedClinic]);

  useEffect(() => {
    if (!selectedClinic) return;
    if (selectedClinic.serviceType !== ServiceType.AGENDA_PROFISSIONAIS) return;
    loadScheduleSlots();
  }, [loadScheduleSlots, selectedClinic]);

  // Convert compact filter UI data → API filters
  const handleFilter = useCallback((data: SlotFiltersData) => {
    const filters: ScheduleSlotFilters = {};

    if (data.searchTerm) filters.search = data.searchTerm;
    if (data.companyId) filters.companyId = Number(data.companyId);
    if (data.status) filters.status = data.status as ScheduleSlotFilters['status'];
    if (data.appointmentType) filters.appointmentType = data.appointmentType as ScheduleSlotFilters['appointmentType'];
    if (data.startDate) filters.startDate = data.startDate;
    if (data.endDate) filters.endDate = data.endDate;

    setApiFilters(filters);
    setPage(0);
  }, []);

  const handleDelete = async (id: number) => {
    if (!window.confirm('Tem certeza que deseja excluir este slot de horário?')) {
      return;
    }

    try {
      await scheduleSlotService.deleteScheduleSlot(id);
      loadScheduleSlots();
    } catch (err: unknown) {
      const prerequisiteBlock = prerequisiteFromError(err);
      if (prerequisiteBlock) {
        setError(null);
        setPrerequisite(prerequisiteBlock);
        return;
      }

      const error = err as Error;
      setError(error.message ?? 'Erro ao excluir slot de horário');
    }
  };

  const handleBlockClick = (id: number) => {
    setBlockingSlotId(id);
    setBlockReason('');
    setBlockDialogOpen(true);
  };

  const handleBlockConfirm = async () => {
    if (!blockingSlotId) return;

    try {
      await scheduleSlotService.blockSlot(blockingSlotId, blockReason);
      setBlockDialogOpen(false);
      setBlockingSlotId(null);
      setBlockReason('');
      loadScheduleSlots();
    } catch (err: unknown) {
      const error = err as Error;
      setError(error.message ?? 'Erro ao bloquear slot');
    }
  };

  const handleUnblock = async (id: number) => {
    try {
      await scheduleSlotService.unblockSlot(id);
      loadScheduleSlots();
    } catch (err: unknown) {
      const error = err as Error;
      setError(error.message ?? 'Erro ao desbloquear slot');
    }
  };

  const isAgendaProfissionais = selectedClinic?.serviceType === ServiceType.AGENDA_PROFISSIONAIS;

  return (
    <PageShell
      breadcrumbs={[{ label: 'Início', path: '/' }, { label: 'Horários Disponíveis' }]}
      title="Horários Disponíveis"
      actions={
        <Tooltip title={HELP_TEXT} arrow placement="bottom-start">
          <InfoOutlined sx={{ fontSize: '1.1rem', color: 'text.secondary', cursor: 'help' }} />
        </Tooltip>
      }
    >
      {/* Clinic selector */}
      {mostrarSeletorDeClinica && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <FormControl fullWidth>
              <InputLabel>Selecionar Clínica</InputLabel>
              <Select
                value={selectedClinic?.id ?? ''}
                onChange={(e) => {
                  const value = e.target.value;
                  const clinic = value ? clinics.find((c: ClinicForSchedule) => c.id === Number(value)) : null;
                  selecionarClinica(clinic ?? null);
                }}
                label="Selecionar Clínica"
                disabled={clinicas.carregando || !clinicas.permitido}
              >
                {clinics.map((clinic: ClinicForSchedule) => (
                  <MenuItem key={clinic.id} value={clinic.id}>
                    {clinic.tradeName}
                  </MenuItem>
                ))}
              </Select>
              {clinicas.bloqueio && <FormHelperText>{clinicas.bloqueio}</FormHelperText>}
            </FormControl>
          </CardContent>
        </Card>
      )}

      {prerequisite && (
        <Box sx={{ mb: 3 }}>
          <PrerequisiteBlock {...prerequisite} />
        </Box>
      )}

      {/* Error message */}
      {error && !loading && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {selectedClinic ? (
        !isAgendaProfissionais ? (
          /* Service type guidance alert */
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              A clínica <strong>{selectedClinic.tradeName}</strong> utiliza o tipo de atendimento <strong>{selectedClinic.serviceType || 'não definido'}</strong>.
            </Typography>
            <Typography variant="body2" gutterBottom>
              Os horários disponíveis são gerados apenas para clínicas com o tipo <strong>{ServiceType.AGENDA_PROFISSIONAIS}</strong>.
            </Typography>
            {selectedClinic.serviceType === ServiceType.AGENDA_CLINICA ? (
              <Typography variant="body2">
                Para editar os horários de atendimento desta clínica, acesse a <strong>listagem de prestadores</strong> e clique no ícone de relógio ao lado do prestador.
              </Typography>
            ) : selectedClinic.serviceType === ServiceType.ORDEM_CHEGADA ? (
              <Typography variant="body2">
                Para editar os horários de atendimento desta clínica, acesse o formulário de edição do prestador na aba <strong>Configuração de Atendimento</strong>.
              </Typography>
            ) : (
              <Typography variant="body2">
                Para configurar, acesse o cadastro do prestador e defina o campo <strong>Tipo de Atendimento</strong>.
              </Typography>
            )}
          </Alert>
        ) : (
          <>
            {/* Filters - compact inline */}
            <Box sx={{ mb: 3 }}>
              <FiltersComponent
                onFilter={handleFilter}
                companies={[]}
                loading={loading}
                total={total}
              />
            </Box>

            {/* Table */}
            <DataTable
              columns={slotColumns}
              rows={scheduleSlots}
              getRowId={(slot) => slot.id}
              loading={loading}
              emptyState={{
                title: 'Nenhum horário disponível ainda',
                description:
                  'Horários são gerados a partir da escala de atendimento de um profissional.',
                prereqChain: 'schedule-slots',
                action: {
                  label: 'Gerar horários',
                  node: 'schedule-slots',
                  deniedHint: 'Peça ao administrador da sua clínica para gerar os horários.',
                },
              }}
              filtersActive={Object.keys(apiFilters).length > 0}
              actions={[
                {
                  icon: <BlockIcon fontSize="small" />,
                  color: 'warning',
                  onClick: (slot) => handleBlockClick(slot.id),
                  hidden: (slot) => !canBlockSlot(slot),
                  ...bloquear,
                },
                {
                  icon: <UnblockIcon fontSize="small" />,
                  color: 'success',
                  onClick: (slot) => handleUnblock(slot.id),
                  hidden: (slot) => !canUnblockSlot(slot),
                  ...desbloquear,
                },
                {
                  icon: <DeleteIcon fontSize="small" />,
                  color: 'error',
                  onClick: (slot) => handleDelete(slot.id),
                  hidden: (slot) => !canDeleteSlot(slot),
                  ...excluir,
                },
              ]}
              pagination={{
                page,
                rowsPerPage,
                totalCount: total,
                onPageChange: setPage,
                onRowsPerPageChange: (value) => {
                  setRowsPerPage(value);
                  setPage(0);
                },
              }}
            />
          </>
        )
      ) : (
        /* Placeholder when no clinic selected */
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">
              Selecione uma clínica para visualizar os horários disponíveis.
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Block Dialog */}
      <Dialog open={blockDialogOpen} onClose={() => setBlockDialogOpen(false)}>
        <DialogTitle>Bloquear Horário</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Motivo do bloqueio (opcional)"
            value={blockReason}
            onChange={(e) => setBlockReason(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBlockDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleBlockConfirm} variant="contained" color="warning">
            Bloquear
          </Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
};

export default ScheduleSlotsListPage;
